import { create } from 'zustand';
import { invitationAPI } from '../lib/api';
import { getChannel } from '../lib/realtime';
import { useServerStore } from './serverStore';

interface Invitation {
  code: string;
  server_id: string;
  creator_id: string;
  max_uses: number;
  max_age: number;
  uses: number;
  created_at: string;
  expires_at: string | null;
  server_info: {
    id: string;
    name: string;
    icon_url: string | null;
    banner_url?: string | null;
    description: string | null;
    member_count: number;
    online_count?: number;
    has_minecraft?: boolean;
  };
  server?: {
    id: string;
    name: string;
    icon_url: string | null;
    banner_url?: string | null;
    description: string | null;
    member_count: number;
    online_count?: number;
    has_minecraft?: boolean;
  };
}

interface InvitationState {
  serverInvitations: Record<string, Invitation[]>;
  currentInvitation: Invitation | null;
  isLoading: boolean;
  error: string | null;
  
  // Server invitation operations
  createInvitation: (serverId: string, maxUses?: number, maxAge?: number) => Promise<Invitation | null>;
  fetchServerInvitations: (serverId: string) => Promise<Invitation[]>;
  revokeInvitation: (inviteCode: string) => Promise<void>;
  
  // Invitation acceptance
  getInvitationInfo: (inviteCode: string) => Promise<Invitation | null>;
  acceptInvitation: (inviteCode: string) => Promise<boolean>;
  
  // UI state management
  setCurrentInvitation: (invitation: Invitation | null) => void;
  subscribeToInvitationEvents: (serverId: string) => () => void;
  
  // Deep linking
  handleInviteLink: (inviteUrl: string) => Promise<void>;
  isElectronInstalled: () => boolean;
  openInElectron: (inviteCode: string) => boolean;
}

export const useInvitationStore = create<InvitationState>((set, get) => ({
  serverInvitations: {},
  currentInvitation: null,
  isLoading: false,
  error: null,
  
  createInvitation: async (serverId, maxUses = 0, maxAge = 0) => {
    try {
      set({ isLoading: true, error: null });
      
      const invitation = await invitationAPI.createInvitation(serverId, maxUses, maxAge);
      
      // Update the server invitations list
      set(state => ({
        serverInvitations: {
          ...state.serverInvitations,
          [serverId]: [...(state.serverInvitations[serverId] || []), invitation]
        }
      }));
      
      return invitation;
    } catch (error: any) {
      console.error('Error creating invitation:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to create invitation' });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },
  
  fetchServerInvitations: async (serverId) => {
    try {
      set({ isLoading: true, error: null });
      
      const invitations = await invitationAPI.getServerInvitations(serverId);
      
      set(state => ({
        serverInvitations: {
          ...state.serverInvitations,
          [serverId]: invitations
        }
      }));
      
      return invitations;
    } catch (error: any) {
      console.error('Error fetching server invitations:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to fetch server invitations' });
      return [];
    } finally {
      set({ isLoading: false });
    }
  },
  
  revokeInvitation: async (inviteCode) => {
    try {
      set({ isLoading: true, error: null });
      
      await invitationAPI.revokeInvitation(inviteCode);
      
      // Remove the invitation from all server invitation lists
      set(state => {
        const newServerInvitations = { ...state.serverInvitations };
        
        for (const serverId in newServerInvitations) {
          newServerInvitations[serverId] = newServerInvitations[serverId].filter(
            invite => invite.code !== inviteCode
          );
        }
        
        return { serverInvitations: newServerInvitations };
      });
    } catch (error: any) {
      console.error('Error revoking invitation:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to revoke invitation' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  getInvitationInfo: async (inviteCode) => {
    try {
      set({ isLoading: true, error: null });
      
      const invitation = await invitationAPI.getInvitationInfo(inviteCode);
      set({ currentInvitation: invitation });
      
      return invitation;
    } catch (error: any) {
      console.error('Error getting invitation info:', error);
      set({
        currentInvitation: null,
        error: error.response?.data?.message || error.message || 'Invalid invitation code',
      });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },
  
  acceptInvitation: async (inviteCode) => {
    let serverId = '';

    try {
      set({ isLoading: true, error: null });
      
      const result = await invitationAPI.acceptInvitation(inviteCode);

      serverId = result?.server_id || result?.server?.id || '';
      if (!serverId) {
        return false;
      }
    } catch (error: any) {
      if (error?.response?.status === 409) {
        const invitation = get().currentInvitation || await get().getInvitationInfo(inviteCode);
        serverId = invitation?.server_id || invitation?.server?.id || invitation?.server_info?.id || '';
      }

      if (!serverId) {
        console.error('Error accepting invitation:', error);
        set({ error: error.response?.data?.message || error.message || 'Failed to accept invitation' });
        return false;
      }
    } finally {
      set({ isLoading: false });
    }

    try {
      const serverStore = useServerStore.getState();
      await serverStore.fetchUserServers();

      const joinedServer = useServerStore.getState().servers.find((s) => s.id === serverId) || null;
      if (!joinedServer) {
        set({ error: 'Server joined, but it could not be opened.' });
        return false;
      }

      useServerStore.setState({
        currentChannel: null,
        channels: [],
        members: [],
        roles: [],
      });
      useServerStore.getState().setCurrentServer(joinedServer);

      await useServerStore.getState().fetchServerChannels(serverId);
      useServerStore.getState().setCurrentChannel(null);
      useServerStore.getState().fetchServerMembers(serverId).catch(console.error);
      useServerStore.getState().fetchServerRoles(serverId).catch(console.error);

      return true;
    } catch (error: any) {
      console.error('Error opening invited server:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to open invited server' });
      return false;
    }
  },
  
  setCurrentInvitation: (invitation) => {
    set({ currentInvitation: invitation });
  },
  
  subscribeToInvitationEvents: (serverId) => {
    const channel = getChannel(`server:${serverId}`);
    if (!channel) return () => {};
    
    const createRef = channel.on('invitation_created', (invitation: Invitation) => {
      if (invitation.server_id === serverId) {
        set(state => ({
          serverInvitations: {
            ...state.serverInvitations,
            [serverId]: [...(state.serverInvitations[serverId] || []), invitation]
          }
        }));
      }
    });
    
    const revokeRef = channel.on('invitation_revoked', (data: { code: string, server_id: string }) => {
      if (data.server_id === serverId) {
        set(state => ({
          serverInvitations: {
            ...state.serverInvitations,
            [serverId]: (state.serverInvitations[serverId] || []).filter(
              invite => invite.code !== data.code
            )
          }
        }));
      }
    });
    
    return () => {
      channel.off('invitation_created', createRef);
      channel.off('invitation_revoked', revokeRef);
    };
  },
  
  // Extract invite code from URL
  handleInviteLink: async (inviteUrl) => {
    const pattern = /erozion\.net\/invite\/([a-zA-Z0-9]+)|erozion:\/\/invite\/([a-zA-Z0-9]+)/;
    const match = inviteUrl.match(pattern);
    
    if (!match) {
      set({ error: 'Invalid invitation link format' });
      return;
    }
    
    const inviteCode = match[1] || match[2];
    
    try {
      const invitation = await get().getInvitationInfo(inviteCode);
      
      if (invitation) {
        await get().acceptInvitation(inviteCode);
      }
    } catch (error: any) {
      console.error('Error handling invitation link:', error);
      set({ error: 'Failed to process invitation link' });
    }
  },
  
  isElectronInstalled: () => {
    // Check if we're already in Electron
    if (window.api) return true;
    
    // Try to detect if the Electron app is installed
    // This is a basic implementation - in a real app, you might use a more robust detection method
    // For example, checking for a cookie set by the Electron app
    try {
      // Try to open the custom protocol in a hidden iframe
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      iframe.src = 'erozion://check';
      
      // Remove the iframe after a short delay
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 100);
      
      return true;
    } catch (error) {
      return false;
    }
  },
  
  openInElectron: (inviteCode) => {
    if (window.api) {
      // Already in Electron, just handle the invite
      get().getInvitationInfo(inviteCode)
        .then(invitation => {
          if (invitation) {
            get().acceptInvitation(inviteCode);
          }
        });
      return true;
    }
    
    // Try to open the Electron app with the invitation link
    try {
      window.location.href = `erozion://invite/${inviteCode}`;
      return true;
    } catch (error) {
      console.error('Error opening Electron app:', error);
      return false;
    }
  }
}));

// Register protocol handler for web app
if (typeof navigator !== 'undefined' && navigator.registerProtocolHandler) {
  try {
    navigator.registerProtocolHandler(
      'erozion',
      `${window.location.origin}/#/invite/%s`,
      'Erozion'
    );
  } catch (error) {
    console.warn('Failed to register protocol handler:', error);
  }
}
